import { useState } from 'react';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Star, Pencil, Trash2, FileText, Search, Users, Building2 } from 'lucide-react';
import { TemplateFormSheet } from './template-form-sheet';
import type { CreateTemplateData } from '@/types/template.types';
import { useTemplates, type ProjectTemplate } from '@/contexts/TemplateContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert a TemplateContext ProjectTemplate into the shape expected by TemplateFormSheet.
 * Since both now share the same canonical type from template.types.ts, this is a
 * straight pass-through with no field renaming needed.
 */
const toFormTemplate = (template: ProjectTemplate): ProjectTemplate => ({ ...template });

// ── Component ─────────────────────────────────────────────────────────────────

interface ManageTemplatesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManageTemplatesSheet({ open, onOpenChange }: ManageTemplatesSheetProps) {
  const { preferences } = useUserPreferences();
  const { templates, addTemplate, updateTemplate, deleteTemplate, toggleFavorite } = useTemplates();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [formSheetOpen, setFormSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ProjectTemplate | null>(null);

  // Determine which side to open from based on sidebar position
  const sheetSide = preferences.sidebarPosition === 'right' ? 'left' : 'right';

  // Filter templates based on search against templateName and templateDescription
  const filteredTemplates = templates.filter(
    (template) =>
      template.templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.templateDescription?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setSheetMode('create');
    setFormSheetOpen(true);
  };

  const handleEditTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setSheetMode('edit');
    setFormSheetOpen(true);
  };

  const handleDeleteClick = (template: ProjectTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.id);
      toast.success('Template deleted successfully');
      setTemplateToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const handleSaveTemplate = (data: CreateTemplateData) => {
    if (sheetMode === 'create') {
      addTemplate({
        templateName: data.templateName,
        templateDescription: data.templateDescription,
        category: data.category,
        description: data.description,
        priority: data.priority,
        type: data.type,
        defaultStatus: data.defaultStatus,
        defaultDurationDays: data.defaultDurationDays,
        currencyCode: data.currencyCode,
        clients: data.clients,
        costCentres: data.costCentres,
        objectives: data.objectives,
        keyDeliverables: data.keyDeliverables,
        isFavorite: false,
        usageCount: 0,
        lastUsedAt: undefined,
      });
      toast.success('Template created successfully');
    } else if (selectedTemplate) {
      updateTemplate(selectedTemplate.id, {
        templateName: data.templateName,
        templateDescription: data.templateDescription,
        category: data.category,
        description: data.description,
        priority: data.priority,
        type: data.type,
        defaultStatus: data.defaultStatus,
        defaultDurationDays: data.defaultDurationDays,
        currencyCode: data.currencyCode,
        clients: data.clients,
        costCentres: data.costCentres,
        objectives: data.objectives,
        keyDeliverables: data.keyDeliverables,
      });
      toast.success('Template updated successfully');
    }
  };

  const handleToggleFavorite = (templateId: string) => {
    toggleFavorite(templateId);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side={sheetSide} className="w-full sm:max-w-xl p-0 flex flex-col h-full">
          {/* Fixed Header */}
          <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
            <SheetHeader>
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle>Manage Templates</SheetTitle>
                  <SheetDescription>
                    Create, edit, and organise your project templates.
                  </SheetDescription>
                </div>
                <Button size="sm" onClick={handleCreateTemplate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create
                </Button>
              </div>
            </SheetHeader>

            {/* Search Bar */}
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <ScrollArea className="flex-1 min-h-0">
            <ScrollAreaViewport className="h-full max-h-full">
              <div className="px-6 py-4">
                {filteredTemplates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {searchTerm ? 'No templates found' : 'No templates yet'}
                    </h3>
                    <p className="text-muted-foreground max-w-md mb-4">
                      {searchTerm
                        ? 'Try adjusting your search terms'
                        : 'Create your first template to start streamlining project creation'}
                    </p>
                    {!searchTerm && (
                      <Button onClick={handleCreateTemplate}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Template
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="group border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold truncate">{template.templateName}</h4>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 shrink-0 -ml-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFavorite(template.id);
                                }}
                                aria-label={
                                  template.isFavorite ? 'Remove from favorites' : 'Add to favorites'
                                }
                              >
                                <Star
                                  className={cn(
                                    'h-3.5 w-3.5',
                                    template.isFavorite
                                      ? 'text-warning-500 fill-current'
                                      : 'text-muted-foreground',
                                  )}
                                />
                              </Button>
                            </div>
                            {template.templateDescription && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {template.templateDescription}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Template Details */}
                        <div className="space-y-1.5 text-xs text-muted-foreground mb-3">
                          {(template.priority || template.type) && (
                            <div className="flex items-center gap-2">
                              {template.priority && (
                                <Badge variant="outline" className="text-xs">
                                  {template.priority}
                                </Badge>
                              )}
                              {template.type && (
                                <Badge variant="secondary" className="text-xs">
                                  {template.type}
                                </Badge>
                              )}
                            </div>
                          )}

                          {template.clients && (
                            <div className="flex items-center gap-2">
                              <Users className="h-3 w-3 shrink-0" />
                              <span className="truncate">1 client</span>
                            </div>
                          )}

                          {template.costCentres && template.costCentres.length > 0 && (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {template.costCentres.length} cost centre
                                {template.costCentres.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleEditTemplate(template)}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteClick(template)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Template Form Sheet */}
      <TemplateFormSheet
        open={formSheetOpen}
        onOpenChange={setFormSheetOpen}
        mode={sheetMode}
        template={selectedTemplate ? toFormTemplate(selectedTemplate) : undefined}
        onSave={handleSaveTemplate}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{templateToDelete?.templateName}&quot;? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
